// NEW_FILE_JUSTIFIED: New page component for unified inbox — combines Telegram and Gmail messages into a single chronological list. No existing page covers this cross-source view.
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useUnifiedInbox } from '@/hooks/useUnifiedInbox';
import { UnifiedInboxItem } from '@/components/messages/UnifiedInboxItem';
import type { IUnifiedInboxItem } from '@/@types/messages';

export function UnifiedInboxPage() {
  const { items, isLoading } = useUnifiedInbox();
  const navigate = useNavigate();

  const handleItemClick = useCallback(
    (item: IUnifiedInboxItem) => {
      if (item.source === 'telegram' && item.telegramContactId) {
        navigate(`/messages/telegram?contact=${encodeURIComponent(item.telegramContactId)}`);
      } else if (item.source === 'gmail' && item.gmailThreadId) {
        navigate(`/messages/gmail/thread/${encodeURIComponent(item.gmailThreadId)}`);
      } else if (item.source === 'whatsapp' && item.whatsappContactId) {
        navigate(`/messages/whatsapp?contact=${encodeURIComponent(item.whatsappContactId)}`);
      }
    },
    [navigate]
  );

  return (
    <main className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-border shrink-0">
        <h1 className="text-2xl font-bold text-foreground">Messages</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div role="status" className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Loading messages</span>
          </div>
        ) : items.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-12 text-muted-foreground"
            aria-live="polite"
          >
            <p className="text-sm">No messages yet</p>
            <p className="text-xs mt-1">Messages from Telegram and Gmail will appear here</p>
          </div>
        ) : (
          <ul className="divide-y divide-border" role="list">
            {items.map(item => (
              <li key={item.id}>
                <UnifiedInboxItem item={item} onClick={handleItemClick} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
